/* eslint-disable no-nested-ternary */
import { TrashIcon } from "@heroicons/react/20/solid";
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  memo,
} from "react";
import SingleSelectDropdown from "../ui/dropdown/SingleSelectDropdown";
import BooleanFilter from "./BooleanFilter";
import DateFilter from "./DateFilter";
import NumberFilter from "./NumberFilter";
import StringFilter from "./StringFilter";
import MemberFilter from "./MemberFilter";
import { determineEntityFromField, getAllFieldsForEntity, type EntityType } from "./utils/entityUtils";

type Props = {
  onRemove: (currentKey: number, filterId?: number) => void;
  currentKey: number;
  workspaceSchema: any;
  filterId: number;
  onCompleteFilter: (
    field: any,
    operator: any,
    value: any,
    type: any,
    entityType: any,
    id: number,
    filterId: number,
  ) => void;
  filter: any;
  entityRelation?: EntityType;
};

const FilterCondition = memo(({
  onRemove,
  filter,
  workspaceSchema,
  currentKey,
  onCompleteFilter,
  filterId,
  entityRelation,
}: Props) => {
  // Entity state - only needed for user manual selection (cross-entity filters)
  const [localEntity, setLocalEntity] = useState<EntityType | null>(null);

  // Memoize derived entity to avoid recalculation
  const derivedEntity = useMemo(
    () => determineEntityFromField(filter?.field, entityRelation, workspaceSchema),
    [filter?.field, entityRelation, workspaceSchema]
  );

  // Compute current entity: use local if set, otherwise derive from filter/entityRelation
  const currentEntity = localEntity || derivedEntity;

  // Get all fields (fields + metrics merged) matching Edit Columns data source
  const allFields = useMemo(
    () => getAllFieldsForEntity(currentEntity, workspaceSchema),
    [currentEntity, workspaceSchema]
  );

  // Memoize allColumns for field lookup
  const allColumns = useMemo(
    () => allFields?.obj || {},
    [allFields?.obj]
  );

  // Split into traits and success metrics (same as AccountControls)
  const filteredTraits = useMemo(
    () => allFields?.list?.filter((item: any) =>
      !item.exclude_display
      && item?.attribute_type !== "metric_traits"
      && item?.generation_type !== "computed_trait"
    ) || [],
    [allFields?.list]
  );

  const filteredSuccessMetrics = useMemo(
    () => allFields?.list?.filter((item: any) =>
      !item.exclude_display && item?.generation_type === "computed_trait"
    ) || [],
    [allFields?.list]
  );

  // Reset local entity when filter field or entityRelation changes significantly
  // Consolidated to prevent infinite loop from circular dependencies
  useEffect(() => {
    if (!filter?.field) {
      // If filter is cleared, reset to entityRelation
      if (localEntity !== null) {
        setLocalEntity(null);
      }
      return;
    }

    // If derived entity matches entityRelation, reset local override
    // Only check derivedEntity if we have a localEntity set
    if (localEntity !== null && derivedEntity === entityRelation) {
      setLocalEntity(null);
    }
  }, [filter?.field, entityRelation, derivedEntity]);

  // Get current field from filter (controlled component - no local state)
  const currentField = useMemo(() => {
    if (!filter?.field) {
      return null;
    }

    let fieldData = allColumns[filter.field];
    const fallbackNames = [
      filter.field,
      String(filter.field || "").replace(/^contacts\./, "users."),
      String(filter.field || "").replace(/^users\./, "contacts."),
    ].filter(Boolean);

    // If not found in local groups, search globally in schema fields and metrics
    if (!fieldData && workspaceSchema) {
      const globalFields = workspaceSchema?.fields?.obj || {};
      const globalMetrics = workspaceSchema?.metrics?.obj || {};
      const matchedField = fallbackNames.find((candidate: string) => (
        globalFields[candidate] || globalMetrics[candidate]
      ));
      if (matchedField) {
        fieldData = globalFields[matchedField] || globalMetrics[matchedField];
      }
    }

    if (!fieldData) {
      return {
        name: filter.field,
        display_name: filter.field,
        id: 5000,
        attribute_type: filter?.type || "string",
        entity_type: filter?.entity_type || entityRelation || "column",
        data_type: filter?.type || "string",
        generation_type: undefined,
      };
    }

    return {
      name: fieldData?.name || filter.field,
      display_name: fieldData?.display_name || filter.field,
      id: 5000,
      attribute_type: fieldData?.attribute_type || filter?.type,
      entity_type: filter?.entity_type || fieldData?.entity_type,
      data_type: fieldData?.data_type || filter?.type,
      generation_type: fieldData?.generation_type,
    };
  }, [filter?.field, filter?.entity_type, filter?.type, allColumns, workspaceSchema, entityRelation]);

  // Handle field change - immediately call onCompleteFilter
  const handleFieldChange = useCallback((newField: any) => {
    if (newField?.name) {
      // When field changes, update the filter with new field
      // Clear operator and value when field changes
      onCompleteFilter(
        newField.name,
        "",
        undefined,
        newField.generation_type === "computed_trait" ? "int" : newField.data_type,
        "column",
        currentKey,
        filterId,
      );
    } else if (!newField) {
      // Field was cleared
      onCompleteFilter(
        "",
        "",
        undefined,
        "string",
        "column",
        currentKey,
        filterId,
      );
    }
  }, [onCompleteFilter, currentKey, filterId]);

  // Handle filter value change
  // Note: We always call onCompleteFilter to update the store for UI state
  // Incomplete filters are filtered out at the API level before making calls
  // See filterIncompletePredicates utility function
  const onCompleteRow = useCallback((
    operator: any,
    value: any,
    type: any,
    entityType: any,
  ) => {
    if (!currentField?.name) return;

    onCompleteFilter(
      currentField.name,
      operator,
      value,
      type,
      entityType,
      currentKey,
      filterId,
    );
  }, [currentField, onCompleteFilter, currentKey, filterId]);

  // Memoize handleRemove callback
  const handleRemoveClick = useCallback(() => {
    onRemove(currentKey, filterId);
  }, [onRemove, currentKey, filterId]);

  // Memoize renderFilter to prevent unnecessary recalculations
  const renderFilter = useMemo(() => {
    if (!currentField?.name) {
      return null;
    }

    const commonProps = {
      fieldSelect: false,
      handleChange: onCompleteRow,
      op: filter?.operator,
      value: filter?.value,
    };

    // Computed traits are always numeric columns — render as NumberFilter
    if (currentField.generation_type === "computed_trait") {
      return (
        <NumberFilter
          key={`computed-trait-${currentField.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (
      currentField.name === "accounts.csm_id" ||
      currentField.name === "users.csm_id" ||
      currentField.name === "accounts.ae_id" ||
      currentField.name === "users.ae_id"
    ) {
      return (
        <MemberFilter
          key={`member-${currentField.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (currentField.data_type === "int" || currentField.data_type === "float") {
      return (
        <NumberFilter
          key={`number-${currentField.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (currentField.data_type === "timestamp" || currentField.data_type === "datetime") {
      return (
        <DateFilter
          key={`date-${currentField.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (currentField.data_type === "boolean") {
      return (
        <BooleanFilter
          key={`boolean-${currentField.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    return (
      <StringFilter
        key={`string-${currentField.name}-${filterId}`}
        fieldSelect={commonProps.fieldSelect}
        handleChange={commonProps.handleChange}
        op={commonProps.op}
        value={commonProps.value}
        selectedField={currentField.name}
      />
    );
  }, [
    currentField,
    filter?.operator,
    filter?.value,
    filterId,
    onCompleteRow,
  ]);

  return (
    <div className="flex gap-2 items-center w-full group">
      {currentKey > 0 && (
        <div className="w-8 flex-shrink-0">
          <span className="text-xs font-medium text-muted-foreground">
            AND
          </span>
        </div>)}

      <div className="flex-1 flex gap-2 items-center border border-border rounded-md p-2 bg-muted/50 hover:border-border/80 transition-colors">
        {/* Entity Select - Hidden or Styled if needed, keeping simple for now as per design */}
        {/* <Select ... /> */}

        <SingleSelectDropdown
          list={filteredTraits}
          type={currentEntity}
          successMetricsList={filteredSuccessMetrics}
          selected={currentField}
          setSelected={handleFieldChange}
          position="bottom-start"
        />
        {renderFilter}
        <div className="ml-auto flex items-center gap-2 pr-2">
          {filter?.value !== "" && filter?.value !== undefined && filter?.value !== null && (
            <div className="bg-green-500 rounded-full p-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-white">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={handleRemoveClick}
        className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
      >
        <span className="sr-only">close</span>
        <TrashIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
});

FilterCondition.displayName = "FilterCondition";

export default FilterCondition;
